import React, { useEffect, useState, useCallback } from "react";
import Layout from "../Component/Layout";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { ORDER_API, PURCHASE_API, MASTER_API } from "../apiConfig";

// Chips are both draggable (drag-to-schedule) and clickable (open
// OrderDetailPopup). Some browsers still fire a "click" on the source
// element some time after a completed HTML5 drag/drop, which would pop
// the full-screen OrderDetailPopup overlay open on top of the calendar.
// Tracked as a timestamp (not a boolean) so the guard window is
// explicit and easy to widen if a slower browser needs more room.
let lastChipDragEndAt = 0;
const CHIP_DRAG_CLICK_GUARD_MS = 1000;

// ── TODO: point these at your real routes ───────────────────────────
// Production Order page already supports `?pod=<orderNo>` to auto-open
// that order in view mode (see ProductionOrder.js). Planned Order's
// equivalent param may be different — adjust PLANNED_ORDER_PARAM below
// if it uses e.g. `?view=` instead of `?pod=`.
// PRODUCTION_ORDER_ROUTE and PLANNED_ORDER_ROUTE are both currently
// unused — both Production Order and Planned Order clicks open the
// inline popup instead (see openOrder below) because navigating to
// either page was logging the user out. Left in place for when each
// page's own bug is fixed and navigation can be restored.
const PRODUCTION_ORDER_ROUTE = "/cable/production/production-order";
const PLANNED_ORDER_ROUTE = "/cable/planning/planned-order";
const PLANNED_ORDER_PARAM = "pod"; // e.g. "view" if that's what Planned Order's page expects

const ORDER_TYPE_OPTIONS = [
    { value: "Production Order", label: "Production Order" },
    { value: "Planned Order", label: "Planned Order" },
];

// "Schedule By" filter — Day view only (see CapacityBar below). Picking
// one shows a per-workcenter capacity bar under that day's orders: By
// Meter checks an order's qty against the workcenter's Capacity By
// Length, By Time checks its scheduled duration against Available
// Capacity.
const SCHEDULE_BY_OPTIONS = [
    { value: "meter", label: "By Meter" },
    { value: "time", label: "By Time" },
];

const erpTh = {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 700,
    color: "#475569",
    textAlign: "left",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
};

const erpTd = {
    padding: "9px 12px",
    fontSize: 13,
    color: "#334155",
    borderBottom: "1px solid #f1f5f9",
};

// Same react-select styling used on the Production Order listing page's
// filter bar (see poFilterSelectStyles / inline styles in
// ProductionOrder.js) so both filter bars look identical.
const filterSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 26,
        height: 26,
        fontSize: 11,
        borderColor: state.isFocused ? "#64748b" : "#e2e8f0",
        borderRadius: 5,
        boxShadow: "none",
        cursor: "pointer",
        "&:hover": { borderColor: "#94a3b8" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 6px", height: 26, flexWrap: "nowrap" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 11 }),
    indicatorsContainer: (base) => ({ ...base, height: 26 }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", svg: { width: 10, height: 10 } }),
    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", svg: { width: 10, height: 10 } }),
    indicatorSeparator: () => ({ display: "none" }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 11, whiteSpace: "nowrap" }),
    singleValue: (base) => ({ ...base, fontSize: 11, color: "#1e293b" }),
    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 11, borderRadius: 5, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        fontSize: 11,
        padding: "4px 8px",
        backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
        color: state.isSelected ? "#fff" : "#334155",
    }),
};

const formatDMY = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// rec_start_time/rec_end_time come back from mysql2 as "HH:MM:SS"
// strings (not full datetimes) — format to a plain 24-hour "HH:MM" for
// display without routing through `new Date()`, which would need a
// fake date component just to parse a bare time string.
const formatTime = (t) => {
    if (!t) return "";
    const m = String(t).match(/^(\d{1,2}):(\d{2})/);
    if (!m) return "";
    const h = String(parseInt(m[1], 10)).padStart(2, "0");
    return `${h}:${m[2]}`;
};

// "HH:MM:SS" -> minutes since midnight, for chronological sorting.
// Orders with no time (null/undefined) sort after every timed order in
// the same workcenter row, rather than being scattered in arbitrary
// (insertion) order.
const timeSortKey = (t) => {
    if (!t) return Infinity;
    const m = String(t).match(/^(\d{1,2}):(\d{2})/);
    if (!m) return Infinity;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

// Same parse as timeSortKey but returns null (not Infinity) for
// missing/unparseable times, so callers can tell "no time" apart from
// "sorts last" — the Day timeline needs that distinction to split
// orders into the timed track vs the untimed strip.
const timeToMinutes = (t) => {
    if (!t) return null;
    const m = String(t).match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

// Combines a date value with an "HH:MM:SS" time string into a real
// local Date, for duration math. Missing time falls back to midnight so
// a date-only order still resolves to *some* instant rather than null.
const combineDateTime = (dateVal, timeVal) => {
    const d = parseLocalDate(dateVal);
    if (!d) return null;
    const mins = timeToMinutes(timeVal);
    const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (mins !== null) out.setMinutes(mins);
    return out;
};

// Total scheduled duration of a PL/PR order — from its start date+time
// through its end date+time — as an "Xh" label for the chip tooltip and
// on-screen readout. Falls back between startDate/endDate the same way
// eventDateKeys does when one side is NULL. Returns null when there's
// nothing to compute (missing dates, or a non-positive span).
const formatDurationHours = (order) => {
    const start = combineDateTime(order.startDate || order.endDate, order.startTime);
    const end = combineDateTime(order.endDate || order.startDate, order.endTime);
    if (!start || !end) return null;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return null;
    const hours = Math.round((diffMs / (1000 * 60 * 60)) * 100) / 100;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(2)}h`;
};

// Chip readout that follows the "Schedule By" toggle: By Meter shows
// the order's qty (cable length, matching Workcenter's own "Capacity By
// Length" field and CapacityBar's own "m" unit) instead of duration,
// since hours aren't the relevant number in that mode. Any other mode
// (By Time, or the toggle left Off) falls back to total scheduled
// hours. Returns null when there's nothing to show, same as its
// building blocks.
const formatScheduleReadout = (order, scheduleBy) => {
    if (scheduleBy === "meter") {
        const q = Number(order.qty);
        if (!(q > 0)) return null;
        return `${Number.isInteger(q) ? q : q.toFixed(1)} m`;
    }
    return formatDurationHours(order);
};

// Greedy interval packing: walks time-sorted {startMin, endMin} items
// and drops each into the first lane whose previous occupant has
// already ended, opening a new lane otherwise — the same approach any
// calendar app uses so two orders overlapping in time stack into
// separate rows instead of drawing on top of each other.
const packIntoLanes = (items) => {
    const laneEnds = [];
    const placed = items.map((item) => {
        let lane = laneEnds.findIndex((end) => end <= item.startMin);
        if (lane === -1) {
            lane = laneEnds.length;
            laneEnds.push(item.endMin);
        } else {
            laneEnds[lane] = item.endMin;
        }
        return { ...item, lane };
    });
    return { placed, laneCount: laneEnds.length || 1 };
};

const DAY_TIME_COL_WIDTH = 110;
const DAY_LANE_HEIGHT = 30;
const DAY_LANE_GAP = 6;
const DAY_HOUR_WIDTH = 90; // px per hour on the Day view's scrollable time ruler

// Converts a config-API response (array of strings, or objects carrying
// name/code/cable_type/category/core/colour/value) into {value,label}
// react-select options — identical helper to getPoFilterOptions in
// ProductionOrder.js.
const getFilterOptions = (list) => {
    if (!list || !Array.isArray(list)) return [];
    return list
        .map((item) => {
            if (typeof item === "string") return { value: item, label: item };
            const val = item.name || item.code || item.cable_type || item.category || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        })
        .filter((opt) => opt.value !== "");
};

const OrderTypeBadge = ({ type }) => {
    const isProduction = type === "Production Order";
    return (
        <span
            style={{
                display: "inline-block",
                padding: "3px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                background: isProduction ? "#ede9fe" : "#dcfce7",
                color: isProduction ? "#6d28d9" : "#15803d",
                whiteSpace: "nowrap",
            }}
        >
            {type}
        </span>
    );
};

// ─────────────────────────────────────────────────────────────────────
// SchedulingCalendar — the "Dashboard" tab. Plots each Production/Planned
// order on its startDate, same one-event-per-record idea as Audit
// Calendar's month view, but adds Week and Day granularity (Audit
// Calendar itself only has Month) plus a Year jump, and chips are
// draggable: dropping one on a different day calls onDropOnDay(dateStr)
// which PUTs /scheduling/reschedule and reloads.
// ─────────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_NAMES_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const toDateKey = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Same local-date parsing caution Audit-calendar.service.js's safeDate
// comment calls out — build the key from the date's own y/m/d getters,
// never toISOString(), so an IST (or any UTC+) browser doesn't shift a
// midnight date back a day.
const parseLocalDate = (raw) => {
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
};

// Every day an order spans, inclusive of both ends — so a Production/
// Planned order running Aug 17 to Aug 20 gets a chip on all four days,
// not just the start day. Falls back to whichever of startDate/endDate
// is set when the other is NULL (several current orders have a NULL
// rec_start_date/requirement_date) so it still renders as a valid
// single-day range instead of disappearing from the grid.
const eventDateKeys = (order) => {
    const start = parseLocalDate(order.startDate) || parseLocalDate(order.endDate);
    const end = parseLocalDate(order.endDate) || parseLocalDate(order.startDate);
    if (!start || !end) return [];
    const keys = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    // Guard against a swapped/bad range so this can't loop forever.
    if (cursor > last) return [toDateKey(cursor)];
    // Second guard: a corrupted start/end pair (bad data, wrong year,
    // NULL-fallback mismatch, etc.) can otherwise make this walk years
    // of days one at a time. Since this function reruns on every
    // render (see eventsByDay below), an unbounded loop here doesn't
    // just cost one slow load — it re-freezes the tab on every click
    // or drag. Five years of days is already far more than any real
    // order spans, so this only ever clips genuinely bad data.
    const MAX_DAYS = 1825;
    let guard = 0;
    while (cursor <= last && guard < MAX_DAYS) {
        keys.push(toDateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
        guard++;
    }
    return keys;
};

// Computes the slice of an order's full [startDate+startTime,
// endDate+endTime] span that falls on ONE specific day (dateKey), as
// {segStart, segEnd} minutes-since-midnight for the Day ruler — this is
// what actually makes an order "come in the day from its start date
// start time to its end date end time" rather than just repeating the
// same time-of-day bar on every day it touches:
//   - single-day order (startDate === endDate): the real
//     startTime→endTime window, same as before.
//   - first day of a multi-day order: from startTime through midnight
//     (continuesAfter), since the order keeps running into the next day.
//   - last day of a multi-day order: from midnight through endTime
//     (continuesBefore), picking up where the previous day left off.
//   - any day strictly between: the whole day (continuesBefore AND
//     continuesAfter), since the order is running the entire time.
// Returns null when the order has no time at all (nothing to place on
// the ruler) — those stay in the "No time set" strip.
const getDaySegment = (order, dateKey) => {
    const hasStartTime = timeToMinutes(order.startTime) !== null;
    const hasEndTime = timeToMinutes(order.endTime) !== null;
    if (!hasStartTime && !hasEndTime) return null;

    const startD = parseLocalDate(order.startDate) || parseLocalDate(order.endDate);
    const endD = parseLocalDate(order.endDate) || startD;
    const startKey = startD ? toDateKey(startD) : dateKey;
    const endKey = endD ? toDateKey(endD) : startKey;

    if (startKey !== endKey) {
        if (dateKey === startKey) {
            return { segStart: hasStartTime ? timeToMinutes(order.startTime) : 0, segEnd: 24 * 60, continuesBefore: false, continuesAfter: true };
        }
        if (dateKey === endKey) {
            return { segStart: 0, segEnd: hasEndTime ? timeToMinutes(order.endTime) : 24 * 60, continuesBefore: true, continuesAfter: false };
        }
        // A day strictly between the order's start and end date — it
        // runs the full 24 hours.
        return { segStart: 0, segEnd: 24 * 60, continuesBefore: true, continuesAfter: true };
    }

    // Single-day order.
    let segStart = hasStartTime ? timeToMinutes(order.startTime) : 0;
    let segEnd = hasEndTime ? timeToMinutes(order.endTime) : segStart + 30;
    if (segEnd <= segStart) segEnd = segStart + 30;
    return { segStart, segEnd, continuesBefore: false, continuesAfter: false };
};

// A single draggable order chip — shared by the month grid, the week
// row, and the day list.
const CalendarChip = ({ ev, onOpenOrder, draggingOrder, setDraggingOrder, large, allocationStatus, scheduleBy }) => {
    const isProduction = ev.orderType === "Production Order";
    const isDraggingThis = draggingOrder && draggingOrder.orderNo === ev.orderNo && draggingOrder.orderType === ev.orderType;

    // Once "Schedule By" is active, a chip that's been dragged onto its
    // workcenter's capacity bar is marked yellow — and switches to red
    // once that bar is completely full — overriding the normal
    // Production/Planned purple-vs-green coloring so it's obvious at a
    // glance which orders have already been placed on the bar. null
    // (not allocated, or Schedule By isn't active) falls through to the
    // usual colors.
    const allocBg = allocationStatus === "full" ? "#ef4444" : allocationStatus === "allocated" ? "#fde047" : null;
    const allocColor = allocationStatus === "full" ? "#fff" : allocationStatus === "allocated" ? "#713f12" : null;

    // Total start→end span for this order, shown at the end of the
    // tooltip and (when there's room, in large mode) at the end of the
    // chip's own line.
    const chipDuration = formatScheduleReadout(ev, scheduleBy);

    return (
        <div
            draggable
            onDragStart={(e) => { e.stopPropagation(); setDraggingOrder(ev); }}
            onDragEnd={(e) => {
                e.stopPropagation();
                lastChipDragEndAt = Date.now();
                setDraggingOrder(null);
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (Date.now() - lastChipDragEndAt < CHIP_DRAG_CLICK_GUARD_MS) return;
                onOpenOrder(ev);
            }}
            title={`${ev.orderNo} — ${ev.productCode || ""} ${ev.description ? "— " + ev.description : ""}${chipDuration ? ` — ${chipDuration} total` : ""}`}
            style={{
                fontSize: large ? 12 : 11,
                fontWeight: 600,
                padding: large ? "5px 8px" : "3px 6px",
                borderRadius: 5,
                cursor: "grab",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "flex",
                alignItems: "baseline",
                gap: 5,
                background: allocBg || (isProduction ? "#ede9fe" : "#dcfce7"),
                color: allocColor || (isProduction ? "#6d28d9" : "#15803d"),
                opacity: isDraggingThis ? 0.4 : 1,
            }}
        >
            <span>{ev.orderNo}</span>
            {ev.workcenter && (
                <span style={{ fontWeight: 500, fontSize: large ? 11 : 10, opacity: 0.85 }}>· {ev.workcenter}</span>
            )}
            {large && ev.productCode && (
                <span style={{ fontWeight: 500, fontSize: 11, opacity: 0.85 }}>· {ev.productCode}</span>
            )}
            {large && (ev.startTime || ev.endTime) && (
                <span style={{ fontWeight: 600, fontSize: 11, opacity: 0.85, marginLeft: "auto" }}>
                    {ev.startTime ? formatTime(ev.startTime) : "?"}
                    {ev.endTime ? ` – ${formatTime(ev.endTime)}` : ""}
                    {chipDuration ? ` · ${chipDuration}` : ""}
                </span>
            )}
        </div>
    );
};

// One drop-target day cell — used by both the month grid and the week row.
const CalendarDayCell = ({
    day, inMonth, dayEvents, isToday, isDragOver,
    minHeight, onOpenOrder, draggingOrder, setDraggingOrder, dragOverDay, setDragOverDay, onDropOnDay, scheduleBy,
}) => {
    const key = toDateKey(day);
    return (
        <div
            onDragOver={(e) => {
                e.preventDefault();
                if (dragOverDay !== key) setDragOverDay(key);
            }}
            onDragLeave={() => {
                if (dragOverDay === key) setDragOverDay(null);
            }}
            onDrop={(e) => {
                e.preventDefault();
                onDropOnDay(key);
            }}
            style={{
                minHeight,
                border: "1px solid #f1f5f9",
                padding: "6px 6px",
                background: isDragOver ? "#eff6ff" : inMonth ? "#fff" : "#fafbfc",
                boxSizing: "border-box",
                transition: "background 0.1s",
            }}
        >
            <div
                style={{
                    fontSize: 11,
                    fontWeight: isToday ? 700 : 600,
                    color: isToday ? "#2563eb" : inMonth ? "#475569" : "#cbd5e1",
                    marginBottom: 4,
                }}
            >
                {day.getDate()}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {dayEvents.map((ev, i) => (
                    <CalendarChip
                        key={`${ev.orderType}-${ev.orderNo}-${i}`}
                        ev={ev}
                        onOpenOrder={onOpenOrder}
                        draggingOrder={draggingOrder}
                        setDraggingOrder={setDraggingOrder}
                        scheduleBy={scheduleBy}
                    />
                ))}
            </div>
        </div>
    );
};

// Grey capacity-consumption bar for one workcenter on one day — drawn
// under that row's chips on the Day view only, while "Schedule By" is
// selected in the filter bar. Purely presentational: bar/loadingBar
// come from SchedulingCalendar's centralized dayCapacityBars (shared
// with the chip-highlighting logic above it, so the bar's numbers and
// each chip's yellow/red marking never fall out of sync). Drop a PL/PR
// chip on it to allocate that order's amount (meters or minutes, from
// getScheduleAmount) against the workcenter's capacity — allocated
// segments draw red. An order that doesn't fit in whatever's left is
// rejected outright — no half-filling — and the same is true if it's
// already allocated here, or has no qty/time to schedule with.
const CapacityBar = ({ workcenter, dateKey, scheduleBy, bar, loadingBar, draggingOrder, setDraggingOrder, getScheduleAmount, onAllocated, pxPos, timelineWidthPx, breaks }) => {
    const [dragOver, setDragOver] = useState(false);
    const [dropError, setDropError] = useState("");

    if (loadingBar) {
        return <div style={{ fontSize: 10, color: "#cbd5e1", padding: "5px 0 0" }}>Loading capacity…</div>;
    }
    if (!bar) {
        return (
            <div style={{ fontSize: 10, color: "#cbd5e1", padding: "5px 0 0" }}>
                No {scheduleBy === "meter" ? "Capacity By Length" : "Available Capacity"} set for {workcenter}
            </div>
        );
    }

    const { capacity, allocations, allocatedTotal, remaining } = bar;
    const isFull = remaining <= 0;
    // capacity/allocatedTotal/remaining come from the backend in each
    // mode's own unit (meters for By Meter, minutes for By Time) — only
    // the display here is converted to hours for By Time, to match the
    // "Xh" readout used everywhere else on the chips. By Meter is shown
    // as-is.
    const unit = scheduleBy === "meter" ? "m" : "h";
    const formatAmount = (mins) => {
        if (scheduleBy === "meter") return mins.toFixed(1);
        const hours = Math.round((mins / 60) * 100) / 100;
        return Number.isInteger(hours) ? String(hours) : hours.toFixed(2);
    };

    // Minutes-since-THIS-DAY's-midnight for a block's start/end, clamped
    // to the visible 24-hour ruler. Deliberately NOT d.getHours()*60 +
    // d.getMinutes() — that reads back the wrong position for any
    // stored timestamp that isn't actually on dateKey's calendar date
    // (e.g. a stale row written before the getDaySegment fix, whose
    // start_time sat on the previous day at 22:41 — getHours/getMinutes
    // on that just says "22:41", placing the block near the END of
    // TODAY's ruler instead of clamped to its start). Measuring the
    // real elapsed-minutes distance from dateKey's midnight and
    // clamping to [0, 1440] is correct either way — old mis-dated rows
    // fall to 0 or 1440 instead of landing on a nonsense hour.
    const [dky, dkm, dkd] = dateKey.split("-").map(Number);
    const dayStartMs = dky && dkm && dkd ? new Date(dky, dkm - 1, dkd, 0, 0, 0).getTime() : null;
    const toDayMinutes = (isoVal) => {
        if (dayStartMs == null) return null;
        const d = new Date(isoVal);
        if (isNaN(d.getTime())) return null;
        const mins = (d.getTime() - dayStartMs) / 60000;
        return Math.min(24 * 60, Math.max(0, mins));
    };

    // Break windows for THIS workcenter/date, as minutes-since-midnight
    // ranges — passed down from the Day view (`breaks` prop, built by
    // the SAME breaksInMinutesFor(wc) that draws the full-height grey
    // column behind the chips), so the bar marks the identical break
    // windows rather than recomputing them itself. extendPastBreaks
    // (scheduling.service.js) is what actually keeps a new drop from
    // landing on a break — it stretches the block's end past the break
    // instead of shifting the whole thing later — this overlay is only
    // the visual: it draws on TOP of the red allocation fill so break
    // minutes read as empty even where an allocation block spans them.

    const handleDrop = (e) => {
        e.preventDefault();
        // Without this, the drop event bubbles up to the Day view's
        // own container-level onDrop (which reschedules the order to
        // this date and reloads the whole calendar).
        e.stopPropagation();
        setDragOver(false);
        if (!draggingOrder) return;
        const order = draggingOrder;
        setDraggingOrder(null);

        // Both modes' amounts are computed and sent together now — the
        // two modes share ONE allocation row per order (see
        // scheduling.service.js), so whichever mode you're NOT looking
        // at right now still needs its own amount stored. Only the
        // amount for the mode you actually dropped onto is required;
        // the other is allowed to come back null (e.g. an order with no
        // qty can still be scheduled by time, and vice versa).
        const amountMeter = getScheduleAmount(order, "meter");
        const amountTime = getScheduleAmount(order, "time");
        const activeAmount = scheduleBy === "meter" ? amountMeter : amountTime;
        if (activeAmount == null) {
            setDropError(
                scheduleBy === "meter"
                    ? `${order.orderNo} has no quantity to schedule by meter`
                    : `${order.orderNo} has no start/end time to schedule by time`
            );
            setTimeout(() => setDropError(""), 3500);
            return;
        }

        // The bar is now time-positioned (server chains each drop back-
        // to-back off the previous block's end — see
        // scheduling.service.js's allocateCapacity). It needs the
        // order's duration for TODAY'S SLICE ONLY — not the order's
        // full multi-day start→end — since the bar is per-day. Reuses
        // getDaySegment, the same clipping the order chip itself uses
        // (segStart/segEnd are minutes-since-midnight on dateKey).
        // Using the full multi-day span here was the bug: a "…until
        // 08:09" order that actually started the day before produced a
        // multi-day duration, ballooning the block miles past today's
        // ruler and shoving every later chained block off-screen with
        // it.
        const seg = getDaySegment(order, dateKey);
        if (!seg) {
            setDropError(`${order.orderNo} has no start/end time to schedule by time`);
            setTimeout(() => setDropError(""), 3500);
            return;
        }
        const durationMinutes = seg.segEnd - seg.segStart;
        if (!durationMinutes || durationMinutes <= 0) {
            setDropError(`${order.orderNo} has no duration to place it on the capacity bar timeline`);
            setTimeout(() => setDropError(""), 3500);
            return;
        }
        // orderStartTime is only used when this is the first block on an
        // otherwise-empty bar (see service) — build it as an actual
        // instant on dateKey at segStart minutes. Parsed manually
        // (rather than via parseLocalDate/`new Date(dateKey)`) because
        // "YYYY-MM-DD" parses as UTC midnight, which can land on the
        // wrong LOCAL day — the same pitfall toLocalISODate's comment on
        // the backend calls out.
        const [dy, dm, dd] = dateKey.split("-").map(Number);
        const orderStart = dy && dm && dd ? new Date(dy, dm - 1, dd, 0, seg.segStart) : null;

        axios
            .post(`${ORDER_API}/scheduling/capacity/allocate`, {
                workcenter, date: dateKey, orderNo: order.orderNo, orderType: order.orderType, scheduleBy,
                amountMeter, amountTime, durationMinutes, orderStartTime: orderStart,
            })
            .then(() => {
                if (onAllocated) onAllocated(); // triggers the parent refetch that also updates chip coloring
            })
            .catch((err) => {
                // 409 CAPACITY_EXCEEDED / ALREADY_ALLOCATED (or any
                // other rejection) — the order is left wherever it
                // was; nothing is trimmed to fit.
                setDropError(err.response?.data?.message || `${order.orderNo} didn't fit — not enough capacity left`);
                setTimeout(() => setDropError(""), 3500);
            });
    };

    return (
        <div style={{ marginTop: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9.5, fontWeight: 700, color: "#94a3b8", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.3 }}>
                <span>Capacity · {scheduleBy === "meter" ? "By Meter" : "By Time"}</span>
                <span style={{ color: isFull ? "#dc2626" : "#94a3b8" }}>
                    {formatAmount(allocatedTotal)} / {formatAmount(capacity)} {unit} ({capacity > 0 ? Math.round((allocatedTotal / capacity) * 100) : 0}%)
                </span>
            </div>
            {/* Same width as the order-chip ruler above it (timelineWidthPx/
                pxPos, passed down from the Day view) so blocks line up with
                real clock time instead of being sized by amount÷capacity. */}
            <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                onDragLeave={(e) => { e.stopPropagation(); setDragOver(false); }}
                onDrop={handleDrop}
                title={isFull ? "Bar is full" : `${formatAmount(remaining)} ${unit} remaining — drag a PL/PR order here`}
                style={{
                    position: "relative",
                    height: 22,
                    width: timelineWidthPx,
                    borderRadius: 5,
                    overflow: "hidden",
                    background: "#e2e8f0",
                    border: dragOver ? "2px dashed #64748b" : "1px solid #cbd5e1",
                    boxSizing: "border-box",
                }}
            >
                {allocations.map((a) => {
                    const startMin = toDayMinutes(a.startTime);
                    const endMin = toDayMinutes(a.endTime);
                    if (startMin == null || endMin == null) return null;
                    const left = pxPos(startMin);
                    const width = Math.max(2, pxPos(endMin) - left);
                    return (
                        <div
                            key={a.id}
                            title={`${a.orderNo} · ${formatAmount(a.amount)} ${unit}`}
                            style={{
                                position: "absolute",
                                top: 0,
                                left,
                                width,
                                height: "100%",
                                background: "#ef4444",
                                borderRight: "1px solid rgba(255,255,255,0.6)",
                            }}
                        />
                    );
                })}
                {/* Break-time overlay — drawn ON TOP of the red allocation
                    fill (higher z-index, pointerEvents none) using the same
                    striped pattern as the chip-row break overlay above, and
                    the same pxPos-based left/width, so break minutes read
                    as empty on the capacity bar even when an allocation
                    block happens to span them underneath. */}
                {(breaks || []).map((b, i) => {
                    const left = pxPos(b.startMin);
                    const width = Math.max(1, pxPos(b.endMin) - left);
                    return (
                        <div
                            key={`capacity-break-${i}`}
                            title="Break time"
                            style={{
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                left,
                                width,
                                background: "repeating-linear-gradient(45deg, #94a3b8, #94a3b8 4px, #f1f5f9 4px, #f1f5f9 8px)",
                                opacity: 0.9,
                                pointerEvents: "none",
                                zIndex: 1,
                            }}
                        />
                    );
                })}
            </div>
            {dropError && <div style={{ fontSize: 10, color: "#dc2626", marginTop: 2 }}>{dropError}</div>}
        </div>
    );
};

const SchedulingCalendar = ({
    refDate, granularity, setGranularity, events, loading, errorMsg,
    onPrev, onNext, onToday, onJumpToYear, onJumpToDate, onOpenOrder,
    draggingOrder, setDraggingOrder, dragOverDay, setDragOverDay, onDropOnDay,
    scheduleByFilter, getScheduleAmount, capacityRefreshToken, onAllocated,
    workcenterOptions,
}) => {
    // "Move to next day" picker — Day view only, gated on scheduleByFilter
    // being set since "leftover" only means something relative to a
    // capacity bar (By Meter/By Time) actually being shown.
    const [showMoveModal, setShowMoveModal] = useState(false);
    // Group events by every day they span (start date through end date
    // inclusive), so a multi-day order gets a chip on each day it's
    // running, not just its first day.
    //
    // Memoized on `events` specifically — NOT recomputed on every
    // render. This used to be a plain object rebuilt on every render,
    // which meant every click (opens the detail popup) or every drag
    // (sets draggingOrder) — both top-level state changes — re-ran this
    // full walk over every order. Combined with eventDateKeys' day-by-
    // day while loop, that turned "click or drag" into "redo an
    // expensive full recompute," which is what made the page seize up
    // specifically on interaction rather than on load.
    const eventsByDay = React.useMemo(() => {
        const map = {};
        events.forEach((ev) => {
            const keys = eventDateKeys(ev);
            keys.forEach((key) => {
                if (!map[key]) map[key] = [];
                map[key].push(ev);
            });
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events]);

    const todayKey = toDateKey(new Date());
    const refYear = refDate.getFullYear();

    // ── Capacity bars for the currently-viewed day (Day view only) ─────
    // Centralized here — one fetch per workcenter present on this day —
    // rather than inside CapacityBar itself, so the SAME data drives
    // both the bar's fill and each chip's yellow/red marking below;
    // fetching independently in two places risked them drifting out of
    // sync for a tick after every allocate.
    const dayKeyForBars = toDateKey(refDate);
    const dayEventsForBars = eventsByDay[dayKeyForBars] || [];
    const dayWorkcentersKey = Array.from(new Set(dayEventsForBars.map((e) => e.workcenter).filter(Boolean))).sort().join("|");

    const [dayCapacityBars, setDayCapacityBars] = useState({}); // workcenter -> bar object | null
    const [dayCapacityLoading, setDayCapacityLoading] = useState(false);

    useEffect(() => {
        const workcenters = dayWorkcentersKey ? dayWorkcentersKey.split("|") : [];
        if (!scheduleByFilter || granularity !== "day" || workcenters.length === 0) {
            setDayCapacityBars({});
            return;
        }
        let cancelled = false;
        setDayCapacityLoading(true);
        Promise.all(
            workcenters.map((wc) =>
                axios
                    .get(`${ORDER_API}/scheduling/capacity`, { params: { workcenter: wc, date: dayKeyForBars, scheduleBy: scheduleByFilter.value } })
                    .then((res) => [wc, res.data])
                    // A workcenter with no capacity configured 400s —
                    // treated as "nothing to show" for that row.
                    .catch(() => [wc, null])
            )
        )
            .then((pairs) => {
                if (cancelled) return;
                const map = {};
                pairs.forEach(([wc, bar]) => { map[wc] = bar; });
                setDayCapacityBars(map);
            })
            .finally(() => { if (!cancelled) setDayCapacityLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduleByFilter, granularity, dayKeyForBars, dayWorkcentersKey, capacityRefreshToken]);

    // Whether ev is currently allocated to wc's capacity bar, and
    // whether that bar is completely full — drives the chip's yellow
    // ("allocated") vs red ("bar is now full") override in CalendarChip
    // and the timed-lane chip below.
    const getChipAllocation = (ev, wc) => {
        if (!scheduleByFilter) return null;
        const bar = dayCapacityBars[wc];
        if (!bar) return null;
        const alloc = bar.allocations.find((a) => a.orderNo === ev.orderNo && a.orderType === ev.orderType);
        if (!alloc) return null;
        return bar.remaining <= 0 ? "full" : "allocated";
    };

    const yearOptions = [];
    for (let y = refYear - 5; y <= refYear + 5; y++) yearOptions.push(y);

    // Title text varies by granularity, same as any calendar app's header.
    let title;
    if (granularity === "day") {
        title = `${WEEKDAY_NAMES_FULL[refDate.getDay()]}, ${MONTH_NAMES[refDate.getMonth()]} ${refDate.getDate()}, ${refYear}`;
    } else if (granularity === "week") {
        const weekStart = new Date(refDate);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
        title = sameMonth
            ? `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()} – ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`
            : `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()} – ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`;
    } else {
        title = `${MONTH_NAMES[refDate.getMonth()]} ${refYear}`;
    }

    return (
        <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
            {/* Nav: title, Prev/Today/Next, Year jump, Month/Week/Day toggle */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>{title}</div>
                    <select
                        value={refYear}
                        onChange={(e) => onJumpToYear(e.target.value)}
                        style={{
                            fontSize: 12, fontWeight: 600, color: "#475569", border: "1px solid #e2e8f0",
                            borderRadius: 6, padding: "4px 6px", background: "#fff", cursor: "pointer",
                        }}
                    >
                        {yearOptions.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {/* Day-only actions — UI placeholder, not wired up yet */}
                    {granularity === "day" && (
                        <div style={{ display: "flex", gap: 6 }}>
                            <button
                                style={{
                                    border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600,
                                    cursor: "pointer", background: "#1e3a8a", color: "#fff",
                                }}
                            >
                                DS Strategy
                            </button>
                            <button
                                style={{
                                    border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600,
                                    cursor: "pointer", background: "#1e3a8a", color: "#fff",
                                }}
                            >
                                Reschedule
                            </button>
                        </div>
                    )}

                    {/* Month/Week/Day granularity toggle */}
                    <div style={{ display: "flex", gap: 3, background: "#f1f5f9", borderRadius: 7, padding: 3 }}>
                        {[
                            { key: "month", label: "Month" },
                            { key: "week", label: "Week" },
                            { key: "day", label: "Day" },
                        ].map((g) => (
                            <button
                                key={g.key}
                                onClick={() => setGranularity(g.key)}
                                style={{
                                    border: "none", borderRadius: 5, padding: "5px 11px", fontSize: 11.5, fontWeight: 600,
                                    cursor: "pointer",
                                    background: granularity === g.key ? "#fff" : "transparent",
                                    color: granularity === g.key ? "#1e293b" : "#64748b",
                                    boxShadow: granularity === g.key ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                                }}
                            >
                                {g.label}
                            </button>
                        ))}
                    </div>

                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {/* Jump straight to any date, regardless of granularity */}
                        <input
                            type="date"
                            value={toDateKey(refDate)}
                            onChange={(e) => onJumpToDate(e.target.value)}
                            style={{
                                fontSize: 12, fontWeight: 600, color: "#475569", border: "1px solid #e2e8f0",
                                borderRadius: 6, padding: "4px 6px", background: "#fff", cursor: "pointer",
                            }}
                        />
                        <button onClick={onPrev} style={calNavBtnStyle}>‹</button>
                        <button onClick={onToday} style={{ ...calNavBtnStyle, width: "auto", padding: "0 12px" }}>Today</button>
                        {/* Beside Today, Day view only — see MoveToNextDayModal
                            above. Only makes sense once a capacity bar is
                            actually on screen (scheduleByFilter set). */}
                        {granularity === "day" && scheduleByFilter && (
                            <button
                                onClick={() => setShowMoveModal(true)}
                                style={{
                                    ...calNavBtnStyle, width: "auto", padding: "0 12px",
                                    background: "#1e3a8a", color: "#fff", border: "none",
                                }}
                            >
                                Move
                            </button>
                        )}
                        <button onClick={onNext} style={calNavBtnStyle}>›</button>
                    </div>
                </div>
            </div>

            <MoveToNextDayModal
                open={showMoveModal}
                onClose={() => setShowMoveModal(false)}
                dateKey={toDateKey(refDate)}
                scheduleBy={scheduleByFilter?.value}
                workcenterOptions={workcenterOptions || []}
                onMoved={onAllocated}
            />

            {scheduleByFilter && granularity !== "day" && (
                <div style={{ padding: "8px 20px", color: "#94a3b8", fontSize: 11.5, fontStyle: "italic" }}>
                    Capacity bars ({scheduleByFilter.label}) show on the Day view — switch to Day to see them.
                </div>
            )}

            {errorMsg && (
                <div style={{ padding: "10px 20px", color: "#dc2626", fontSize: 13 }}>{errorMsg}</div>
            )}

            {loading ? (
                <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading calendar...</div>
            ) : granularity === "day" ? (
                // ── Day view: workcenter rows on the Y-axis, a shared 24-hour
                // clock ruler on the X-axis. The ruler is drawn at a fixed
                // px-per-hour width (not squeezed to the container), so on
                // a full or near-full-day span it overflows and the wrapper
                // scrolls horizontally — the workcenter label column is
                // pinned with position:sticky so it stays put while you
                // scroll through the hours. Each order with a startTime is
                // drawn as a bar positioned/sized by its actual start–end
                // time (overlapping ones stack into extra lanes via
                // packIntoLanes); orders with no time at all — every
                // Planned Order today, since planning_elements has no time
                // column yet (see scheduling.service.js) — can't be placed
                // on a clock axis, so they collect in a separate "No time
                // set" strip under each row's ruler instead of being
                // dropped or plotted at a fake time.
                (() => {
                    const key = toDateKey(refDate);
                    const dayEvents = eventsByDay[key] || [];
                    const isDragOver = dragOverDay === key;
                    // Chromium has a known issue where native HTML5 drag
                    // events crossing position:sticky elements inside an
                    // overflow:auto container (this view's pinned ruler +
                    // label column) can make the tab appear to hang mid-
                    // drag — reported as "whole screen stuck" specifically
                    // in Day view, specifically dragging onto/around the
                    // capacity bar underneath the sticky ruler, and NOT
                    // reproducing in other browsers. Dropping sticky to
                    // static only while a drag is actually in progress
                    // sidesteps it with no visible change the rest of the
                    // time (position:sticky only differs from static once
                    // you scroll during a drag, which is a rare/edge case
                    // next to a frozen tab).
                    const isDraggingAny = !!draggingOrder;

                    const UNASSIGNED = "Unassigned";
                    const eventsByWorkcenter = {};
                    dayEvents.forEach((ev) => {
                        const wc = ev.workcenter || UNASSIGNED;
                        if (!eventsByWorkcenter[wc]) eventsByWorkcenter[wc] = [];
                        eventsByWorkcenter[wc].push(ev);
                    });
                    const workcenters = Object.keys(eventsByWorkcenter).sort((a, b) => {
                        if (a === UNASSIGNED) return 1;
                        if (b === UNASSIGNED) return -1;
                        return a.localeCompare(b);
                    });

                    // Split each workcenter's orders into timed (goes on the
                    // ruler) vs untimed (goes in the strip below), and pack
                    // the timed ones into lanes so overlaps stack instead of
                    // overlapping visually.
                    const rows = workcenters.map((wc) => {
                        const all = eventsByWorkcenter[wc];
                        const timedRaw = [];
                        const untimed = [];
                        all.forEach((ev) => {
                            const seg = getDaySegment(ev, key);
                            if (!seg) {
                                untimed.push(ev);
                                return;
                            }
                            timedRaw.push({
                                ev,
                                startMin: seg.segStart,
                                endMin: seg.segEnd,
                                continuesBefore: seg.continuesBefore,
                                continuesAfter: seg.continuesAfter,
                            });
                        });
                        timedRaw.sort((a, b) => a.startMin - b.startMin);
                        const { placed, laneCount } = packIntoLanes(timedRaw);
                        return { wc, timed: placed, laneCount, untimed, total: all.length };
                    });

                    // Ruler range: always the full 24-hour day (00:00–24:00),
                    // regardless of where the day's actual timed orders fall —
                    // keeps the clock ruler and the capacity bar underneath it
                    // (which shares this same width) consistent from day to
                    // day instead of stretching/shrinking with the data.
                    const axisStart = 0;
                    const axisEnd = 24 * 60;
                    const axisSpan = axisEnd - axisStart;
                    // Fixed px-per-hour ruler — NOT a percentage of the
                    // container — so the timeline has a real, scrollable
                    // width instead of being squeezed to fit.
                    const timelineWidthPx = Math.round((axisSpan / 60) * DAY_HOUR_WIDTH);
                    const pxPos = (min) => ((min - axisStart) / axisSpan) * timelineWidthPx;

                    const hourTicks = [];
                    for (let m = axisStart; m <= axisEnd; m += 60) hourTicks.push(m);
                    const gridSegments = Math.max(1, hourTicks.length - 1);

                    // 24-hour "HH:MM" ruler labels.
                    const formatHourLabel = (min) => {
                        const h = Math.floor(min / 60) % 24;
                        return `${String(h).padStart(2, "0")}:00`;
                    };

                    const now = new Date();
                    const isToday = toDateKey(now) === key;
                    const nowMin = now.getHours() * 60 + now.getMinutes();
                    const showNowLine = isToday && nowMin >= axisStart && nowMin <= axisEnd;

                    // This workcenter's break windows on `key`, as
                    // minutes-since-midnight — shared by the full-column
                    // grey overlay AND the order-chip splitting below,
                    // so both agree on exactly where break time sits.
                    const breaksInMinutesFor = (wc) => {
                        const [by, bm, bd] = key.split("-").map(Number);
                        const dayStart = by && bm && bd ? new Date(by, bm - 1, bd, 0, 0, 0).getTime() : null;
                        const toMin = (iso) => {
                            if (dayStart == null || !iso) return null;
                            const d = new Date(iso);
                            if (isNaN(d.getTime())) return null;
                            return Math.min(1440, Math.max(0, (d.getTime() - dayStart) / 60000));
                        };
                        return (dayCapacityBars[wc]?.shifts || [])
                            .flatMap((s) => s.breaks || [])
                            .map((b) => ({ startMin: toMin(b.start), endMin: toMin(b.end) }))
                            .filter((b) => b.startMin != null && b.endMin != null && b.endMin > b.startMin)
                            .sort((a, b) => a.startMin - b.startMin);
                    };

                    // Clips this workcenter's break windows to the part
                    // that falls inside [start,end], for drawing the
                    // striped "break" overlay ON TOP of a chip. Chips
                    // themselves are no longer cut into separate pieces
                    // at break boundaries — the color bar now runs
                    // straight through the whole span (clean, one
                    // piece, no gap); the stripe overlay is what still
                    // marks where break time sits within it.
                    const breaksWithinRange = (start, end, breaks) =>
                        breaks
                            .map((b) => ({ startMin: Math.max(start, b.startMin), endMin: Math.min(end, b.endMin) }))
                            .filter((b) => b.endMin > b.startMin);

                    return (
                        <div
                            onDragOver={(e) => { e.preventDefault(); if (dragOverDay !== key) setDragOverDay(key); }}
                            onDragLeave={() => { if (dragOverDay === key) setDragOverDay(null); }}
                            onDrop={(e) => { e.preventDefault(); onDropOnDay(key); }}
                            style={{ minHeight: 240, padding: "16px 16px 20px", background: isDragOver ? "#eff6ff" : "#fff" }}
                        >
                            {dayEvents.length === 0 ? (
                                <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: 30 }}>
                                    No orders scheduled on this day.
                                </div>
                            ) : (
                                // Self-contained scroll box for BOTH axes — the
                                // ruler (sticky top) and label column (sticky
                                // left) are pinned relative to THIS box's own
                                // scrollport, not the page's. That makes the
                                // freeze effect work no matter what overflow
                                // settings exist on ancestors like Layout.js —
                                // this box scrolls itself, like a spreadsheet's
                                // frozen header/column, rather than depending
                                // on the whole page to be the scroll container.
                                <div style={{ overflow: "auto", maxHeight: "70vh", border: "1px solid #f1f5f9", borderRadius: 8, minWidth: 0 }}>
                                    <div style={{ width: DAY_TIME_COL_WIDTH + timelineWidthPx }}>
                                        {/* Shared clock ruler (X-axis), 24-hour format —
                                            sticky to the top of the scroll area (in
                                            addition to the label column's sticky left)
                                            so both axes stay visible together as you
                                            scroll down through the workcenter rows. */}
                                        <div style={{ display: "flex", position: isDraggingAny ? "static" : "sticky", top: 0, zIndex: 4, background: "#fff" }}>
                                            <div
                                                style={{
                                                    position: isDraggingAny ? "static" : "sticky", left: 0, zIndex: 5,
                                                    width: DAY_TIME_COL_WIDTH, flex: `0 0 ${DAY_TIME_COL_WIDTH}px`,
                                                    background: "#fff", borderRight: "2px solid #e2e8f0", borderBottom: "1px solid #e2e8f0",
                                                }}
                                            />
                                            <div
                                                style={{
                                                    flex: `0 0 ${timelineWidthPx}px`, width: timelineWidthPx,
                                                    position: "relative", height: 22, borderBottom: "1px solid #e2e8f0",
                                                }}
                                            >
                                                {hourTicks.map((m) => (
                                                    <div
                                                        key={m}
                                                        style={{
                                                            position: "absolute",
                                                            left: pxPos(m),
                                                            transform: m === axisStart ? "none" : m === axisEnd ? "translateX(-100%)" : "translateX(-50%)",
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            color: "#94a3b8",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {formatHourLabel(m)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            {rows.map(({ wc, timed, laneCount, untimed, total }) => (
                                                <div
                                                    key={wc}
                                                    style={{ display: "flex", alignItems: "stretch", borderBottom: "1px solid #f8fafc" }}
                                                >
                                                    <div
                                                        style={{
                                                            position: isDraggingAny ? "static" : "sticky", left: 0, zIndex: 2,
                                                            width: DAY_TIME_COL_WIDTH, flex: `0 0 ${DAY_TIME_COL_WIDTH}px`,
                                                            background: "#fff",
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            color: wc === UNASSIGNED ? "#94a3b8" : "#334155",
                                                            padding: "10px 12px 10px 0",
                                                            textAlign: "right",
                                                            borderRight: "2px solid #e2e8f0",
                                                        }}
                                                    >
                                                        {wc}
                                                        <div style={{ fontWeight: 400, color: "#94a3b8", fontSize: 10 }}>
                                                            {total} order{total === 1 ? "" : "s"}
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            flex: `0 0 ${timelineWidthPx}px`, width: timelineWidthPx,
                                                            display: "flex", flexDirection: "column", gap: 6,
                                                            padding: "10px 0 10px 16px",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {/* Time-positioned track */}
                                                        <div
                                                            style={{
                                                                position: "relative",
                                                                height: Math.max(laneCount, 1) * (DAY_LANE_HEIGHT + DAY_LANE_GAP),
                                                                background: `repeating-linear-gradient(to right, #f1f5f9 0, #f1f5f9 1px, transparent 1px, transparent calc(100% / ${gridSegments}))`,
                                                                borderRadius: 6,
                                                            }}
                                                        >
                                                            {/* Break-time overlay — full-height grey column behind
                                                                everything else in this row (chips + capacity bar,
                                                                which reads the same pxPos-based left/width — see the
                                                                CapacityBar component), for whichever break windows
                                                                this workcenter's shift calendar has today. Lives
                                                                INSIDE the track div (not the outer padded container)
                                                                so its pxPos(...) left offset shares the exact same
                                                                positioning context as the chips/on-chip stripes —
                                                                sitting it in the outer container instead left it 16px
                                                                (the container's own padding-left) out of alignment
                                                                with them, since absolute positioning measures from the
                                                                padding edge, not the padded content's start.
                                                                pointerEvents: none so it never intercepts drag/drop. */}
                                                            {wc !== UNASSIGNED && breaksInMinutesFor(wc).map((b, i) => {
                                                                const left = pxPos(b.startMin);
                                                                const width = Math.max(1, pxPos(b.endMin) - left);
                                                                return (
                                                                    <div
                                                                        key={`break-col-${i}`}
                                                                        title="Break time"
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: 0,
                                                                            bottom: 0,
                                                                            left,
                                                                            width,
                                                                            background: "repeating-linear-gradient(45deg, #94a3b8, #94a3b8 4px, #cbd5e1 4px, #cbd5e1 8px)",
                                                                            opacity: 0.35,
                                                                            pointerEvents: "none",
                                                                            zIndex: 0,
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                            {showNowLine && (
                                                                <div
                                                                    style={{
                                                                        position: "absolute", top: 0, bottom: 0,
                                                                        left: pxPos(nowMin), width: 2,
                                                                        background: "#ef4444", zIndex: 2,
                                                                    }}
                                                                    title="Now"
                                                                />
                                                            )}
                                                            {timed.length === 0 ? (
                                                                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", fontSize: 11, color: "#cbd5e1", paddingLeft: 8 }}>
                                                                    No timed orders on the ruler
                                                                </div>
                                                            ) : (
                                                                (() => {
                                                                    const wcBreaks = wc !== UNASSIGNED ? breaksInMinutesFor(wc) : [];
                                                                    return timed.map(({ ev, startMin, endMin, lane, continuesBefore, continuesAfter }, i) => {
                                                                    const isProduction = ev.orderType === "Production Order";
                                                                    const isDraggingThis = draggingOrder && draggingOrder.orderNo === ev.orderNo && draggingOrder.orderType === ev.orderType;
                                                                    const allocationStatus = getChipAllocation(ev, wc);
                                                                    const allocBg = allocationStatus === "full" ? "#ef4444" : allocationStatus === "allocated" ? "#fde047" : null;
                                                                    const allocColor = allocationStatus === "full" ? "#fff" : allocationStatus === "allocated" ? "#713f12" : null;

                                                                    // Total start→end span for this order (across all
                                                                    // its days, not just today's slice) — appended to
                                                                    // the tooltip and shown as the last item on the
                                                                    // chip itself.
                                                                    const durationLabel = formatScheduleReadout(ev, scheduleByFilter?.value);

                                                                    // Full start-datetime → end-datetime range, always
                                                                    // shown in the tooltip regardless of which day/slice
                                                                    // is currently on screen.
                                                                    const fullRangeTitle =
                                                                        `${ev.orderNo} — ${formatDMY(ev.startDate)}${ev.startTime ? " " + formatTime(ev.startTime) : ""}` +
                                                                        ` → ${formatDMY(ev.endDate)}${ev.endTime ? " " + formatTime(ev.endTime) : ""}` +
                                                                        `${ev.workcenter ? " · " + ev.workcenter : ""}` +
                                                                        `${durationLabel ? " · " + durationLabel + " total" : ""}`;

                                                                    // What this particular slice represents on THIS
                                                                    // day — the real start/end time on the order's
                                                                    // first/last day, "all day" for a day it just
                                                                    // runs straight through in between.
                                                                    let sliceLabel;
                                                                    if (!continuesBefore && !continuesAfter) {
                                                                        sliceLabel = `${formatTime(ev.startTime)}${ev.endTime ? "–" + formatTime(ev.endTime) : ""}`;
                                                                    } else if (!continuesBefore && continuesAfter) {
                                                                        sliceLabel = `from ${formatTime(ev.startTime)}`;
                                                                    } else if (continuesBefore && !continuesAfter) {
                                                                        sliceLabel = `until ${formatTime(ev.endTime)}`;
                                                                    } else {
                                                                        sliceLabel = "all day";
                                                                    }

                                                                    // Cut ONLY at day boundaries now (continuesBefore/
                                                                    // continuesAfter) — a break inside [startMin,endMin]
                                                                    // no longer splits the chip into separate pieces
                                                                    // with a gap between them. The bar draws as one
                                                                    // continuous, single-color piece; breaksWithinRange
                                                                    // below supplies the striped overlay drawn on top
                                                                    // of it instead, so the color still reads as one
                                                                    // clean block while the break is still visible.
                                                                    const leftPx = pxPos(startMin);
                                                                    const widthPx = Math.max(50, pxPos(endMin) - pxPos(startMin));
                                                                    const pieceCutBefore = continuesBefore;
                                                                    const pieceCutAfter = continuesAfter;
                                                                    const breakOverlays = breaksWithinRange(startMin, endMin, wcBreaks);

                                                                    return [(
                                                                        <div
                                                                            key={`${ev.orderType}-${ev.orderNo}-${i}`}
                                                                            draggable
                                                                            onDragStart={(e) => { e.stopPropagation(); setDraggingOrder(ev); }}
                                                                            onDragEnd={(e) => {
                                                                                e.stopPropagation();
                                                                                lastChipDragEndAt = Date.now();
                                                                                setDraggingOrder(null);
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (Date.now() - lastChipDragEndAt < CHIP_DRAG_CLICK_GUARD_MS) return;
                                                                                onOpenOrder(ev);
                                                                            }}
                                                                            title={fullRangeTitle}
                                                                            style={{
                                                                                position: "absolute",
                                                                                left: leftPx,
                                                                                width: widthPx,
                                                                                top: lane * (DAY_LANE_HEIGHT + DAY_LANE_GAP),
                                                                                height: DAY_LANE_HEIGHT,
                                                                                // A continuing edge is drawn square + dashed
                                                                                // instead of rounded, to read as "cut off
                                                                                // here, keeps going" at a day boundary.
                                                                                borderTopLeftRadius: pieceCutBefore ? 0 : 5,
                                                                                borderBottomLeftRadius: pieceCutBefore ? 0 : 5,
                                                                                borderTopRightRadius: pieceCutAfter ? 0 : 5,
                                                                                borderBottomRightRadius: pieceCutAfter ? 0 : 5,
                                                                                borderLeft: pieceCutBefore ? "3px dashed rgba(0,0,0,0.22)" : "none",
                                                                                borderRight: pieceCutAfter ? "3px dashed rgba(0,0,0,0.22)" : "none",
                                                                                padding: "0 8px",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                gap: 5,
                                                                                fontSize: 11,
                                                                                fontWeight: 600,
                                                                                whiteSpace: "nowrap",
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis",
                                                                                cursor: "grab",
                                                                                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                                                                background: allocBg || (isProduction ? "#ede9fe" : "#dcfce7"),
                                                                                color: allocColor || (isProduction ? "#6d28d9" : "#15803d"),
                                                                                opacity: isDraggingThis ? 0.4 : 1,
                                                                                zIndex: 1,
                                                                            }}
                                                                        >
                                                                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                                {continuesBefore ? "… " : ""}{ev.orderNo}{continuesAfter ? " …" : ""}
                                                                            </span>
                                                                            <span style={{ opacity: 0.85, fontWeight: 500 }}>· {sliceLabel}</span>
                                                                            {durationLabel && (
                                                                                <span style={{ opacity: 0.7, fontWeight: 500, marginLeft: "auto", flexShrink: 0 }}>
                                                                                    · {durationLabel}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ),
                                                                    // Break stripe(s), drawn ON TOP of the chip
                                                                    // (higher zIndex) instead of cutting a gap out of
                                                                    // it — pointerEvents: none so the chip underneath
                                                                    // still fully handles drag/click.
                                                                    ...breakOverlays.map((b, bi) => (
                                                                        <div
                                                                            key={`${ev.orderType}-${ev.orderNo}-${i}-brk-${bi}`}
                                                                            title="Break time"
                                                                            style={{
                                                                                position: "absolute",
                                                                                left: pxPos(b.startMin),
                                                                                width: Math.max(1, pxPos(b.endMin) - pxPos(b.startMin)),
                                                                                top: lane * (DAY_LANE_HEIGHT + DAY_LANE_GAP),
                                                                                height: DAY_LANE_HEIGHT,
                                                                                // multiply (not a flat grey fill) so this
                                                                                // reads as the chip's OWN color darkened
                                                                                // by the stripe, not a separate grey patch
                                                                                // sitting on top of it — same texture,
                                                                                // whatever the chip's background is.
                                                                                background: "repeating-linear-gradient(45deg, rgba(0,0,0,0.28), rgba(0,0,0,0.28) 3px, transparent 3px, transparent 7px)",
                                                                                mixBlendMode: "multiply",
                                                                                pointerEvents: "none",
                                                                                zIndex: 2,
                                                                            }}
                                                                        />
                                                                    ))];
                                                                })
                                                                })()
                                                            )}
                                                        </div>

                                                        {/* Orders with no start/end time — can't go on the ruler, shown here instead */}
                                                        {untimed.length > 0 && (
                                                            <div>
                                                                <div style={{ fontSize: 9, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>
                                                                    No time set
                                                                </div>
                                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                                                                    {untimed.map((ev, i) => (
                                                                        <CalendarChip
                                                                            key={`${ev.orderType}-${ev.orderNo}-u${i}`}
                                                                            ev={ev}
                                                                            large
                                                                            onOpenOrder={onOpenOrder}
                                                                            draggingOrder={draggingOrder}
                                                                            setDraggingOrder={setDraggingOrder}
                                                                            allocationStatus={getChipAllocation(ev, wc)}
                                                                            scheduleBy={scheduleByFilter?.value}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Capacity bar — after every PL/PR chip for this
                                                            workcenter, only while "Schedule By" is picked */}
                                                        {scheduleByFilter && wc !== UNASSIGNED && (
                                                            <CapacityBar
                                                                workcenter={wc}
                                                                dateKey={key}
                                                                scheduleBy={scheduleByFilter.value}
                                                                bar={dayCapacityBars[wc] || null}
                                                                loadingBar={dayCapacityLoading && !dayCapacityBars[wc]}
                                                                draggingOrder={draggingOrder}
                                                                setDraggingOrder={setDraggingOrder}
                                                                getScheduleAmount={getScheduleAmount}
                                                                onAllocated={onAllocated}
                                                                pxPos={pxPos}
                                                                timelineWidthPx={timelineWidthPx}
                                                                breaks={breaksInMinutesFor(wc)}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()
            ) : granularity === "week" ? (
                // ── Week view: a single row of 7 days ───────────────────────────
                (() => {
                    const weekStart = new Date(refDate);
                    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                    const days = [];
                    for (let i = 0; i < 7; i++) {
                        const d = new Date(weekStart);
                        d.setDate(d.getDate() + i);
                        days.push(d);
                    }
                    return (
                        <div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid #e2e8f0" }}>
                                {days.map((d) => (
                                    <div key={toDateKey(d)} style={{ padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#64748b", textAlign: "center" }}>
                                        {WEEKDAY_NAMES[d.getDay()]} {d.getDate()}
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                                {days.map((day) => (
                                    <CalendarDayCell
                                        key={toDateKey(day)}
                                        day={day}
                                        inMonth
                                        dayEvents={eventsByDay[toDateKey(day)] || []}
                                        isToday={toDateKey(day) === todayKey}
                                        isDragOver={dragOverDay === toDateKey(day)}
                                        minHeight={190}
                                        onOpenOrder={onOpenOrder}
                                        draggingOrder={draggingOrder}
                                        setDraggingOrder={setDraggingOrder}
                                        dragOverDay={dragOverDay}
                                        setDragOverDay={setDragOverDay}
                                        onDropOnDay={onDropOnDay}
                                        scheduleBy={scheduleByFilter?.value}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })()
            ) : (
                // ── Month view: the usual 6-week grid ───────────────────────────
                (() => {
                    const firstOfMonth = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
                    const gridStart = new Date(firstOfMonth);
                    gridStart.setDate(gridStart.getDate() - firstOfMonth.getDay());

                    const weeks = [];
                    let cursor = new Date(gridStart);
                    for (let w = 0; w < 6; w++) {
                        const days = [];
                        for (let d = 0; d < 7; d++) {
                            days.push(new Date(cursor));
                            cursor.setDate(cursor.getDate() + 1);
                        }
                        weeks.push(days);
                    }

                    return (
                        <div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid #e2e8f0" }}>
                                {WEEKDAY_NAMES.map((d) => (
                                    <div key={d} style={{ padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#64748b", textAlign: "center" }}>
                                        {d}
                                    </div>
                                ))}
                            </div>
                            {weeks.map((week, wi) => (
                                <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                                    {week.map((day) => {
                                        const key = toDateKey(day);
                                        return (
                                            <CalendarDayCell
                                                key={key}
                                                day={day}
                                                inMonth={day.getMonth() === refDate.getMonth()}
                                                dayEvents={eventsByDay[key] || []}
                                                isToday={key === todayKey}
                                                isDragOver={dragOverDay === key}
                                                minHeight={80}
                                                onOpenOrder={onOpenOrder}
                                                draggingOrder={draggingOrder}
                                                setDraggingOrder={setDraggingOrder}
                                                dragOverDay={dragOverDay}
                                                setDragOverDay={setDragOverDay}
                                                onDropOnDay={onDropOnDay}
                                                scheduleBy={scheduleByFilter?.value}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    );
                })()
            )}
        </div>
    );
};

const calNavBtnStyle = {
    width: 28,
    height: 28,
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    background: "#fff",
    color: "#475569",
    fontSize: 14,
    cursor: "pointer",
};

// ─────────────────────────────────────────────────────────────────────
// OrderDetailPopup — shown when a Production Order chip/link is clicked,
// in place of navigating to the Production Order page (see openOrder's
// comment above). Leads with Workcenter and Qty since those are what
// was asked for; everything else is supporting context.
// ─────────────────────────────────────────────────────────────────────
const OrderDetailPopup = ({ order, onClose }) => {
    if (!order) return null;

    const rows = [
        { label: "Workcenter", value: order.workcenter || "—" },
        { label: "Qty", value: order.qty != null ? order.qty : "—" },
        { label: "Product Code", value: order.productCode || "—" },
        { label: "Description", value: order.description || "—" },
        { label: "Start Date", value: formatDMY(order.startDate) || "—" },
        // Time rows only show up when the order actually has a time (see
        // scheduling.service.js) — most Production Orders currently
        // don't, so "—" there is accurate, not a missing-data bug.
        { label: "Start Time", value: order.startTime ? formatTime(order.startTime) : "—" },
        { label: "End Date", value: formatDMY(order.endDate) || "—" },
        { label: "End Time", value: order.endTime ? formatTime(order.endTime) : "—" },
        { label: "Status", value: order.status || "—" },
    ];

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#fff", borderRadius: 12, width: 380, maxWidth: "90vw",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.18)", overflow: "hidden",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #f1f5f9" }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>{order.orderNo}</div>
                        <div style={{ marginTop: 4 }}>
                            <OrderTypeBadge type={order.orderType} />
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: "none", background: "transparent", fontSize: 18, color: "#94a3b8",
                            cursor: "pointer", lineHeight: 1, padding: 4,
                        }}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div style={{ padding: "14px 18px" }}>
                    {rows.map((r) => (
                        <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f8fafc" }}>
                            <span style={{ fontSize: 12, color: "#64748b" }}>{r.label}</span>
                            <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1e293b", textAlign: "right" }}>{r.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// MoveToNextDayModal — the Day view's "Move" action, next to Today
// (Day view only — see the nav row in SchedulingCalendar). For when a
// workcenter's capacity bar is full and some PL/PR orders on that
// day's ruler never got dropped onto it: pick a workcenter, see what's
// left over, pick some/all of them, and reschedule them onto the very
// next calendar day, chained back-to-back from that day's midnight.
// Deliberately does NOT touch the capacity bar itself — the new day's
// bar is left empty on purpose (see scheduling.service.js's
// moveOrdersToNextDay), since the user drags each moved order onto it
// themselves afterward, same as any normal allocation. That also means
// this can never fail with a capacity error — every selected order
// always moves.
//
// "Left over" here means exactly what CapacityBar.handleDrop already
// means: on this workcenter's ruler for `dateKey`, but with no row yet
// in scheduling_capacity_allocation there (GET .../capacity/unallocated).
// Fetched fresh every time a workcenter is picked.
const MoveToNextDayModal = ({ open, onClose, dateKey, scheduleBy, workcenterOptions, onMoved }) => {
    const [workcenter, setWorkcenter] = useState("");
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [submitting, setSubmitting] = useState(false);
    const [resultMsg, setResultMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    // Reset on every (re)open so a previous run's picks/messages don't
    // bleed into the next one.
    useEffect(() => {
        if (!open) return;
        setWorkcenter("");
        setOrders([]);
        setSelected(new Set());
        setResultMsg("");
        setErrorMsg("");
    }, [open]);

    useEffect(() => {
        if (!open || !workcenter) { setOrders([]); return; }
        setLoadingOrders(true);
        setErrorMsg("");
        axios
            .get(`${ORDER_API}/scheduling/capacity/unallocated`, { params: { workcenter, date: dateKey, scheduleBy } })
            .then((res) => setOrders(res.data.orders || []))
            .catch((err) => setErrorMsg(err.response?.data?.message || "Failed to load orders for this workcenter"))
            .finally(() => setLoadingOrders(false));
    }, [open, workcenter, dateKey, scheduleBy]);

    if (!open) return null;

    const keyOf = (o) => `${o.orderNo}::${o.orderType}`;
    const allSelected = orders.length > 0 && selected.size === orders.length;

    const toggleOne = (o) => {
        setSelected((prev) => {
            const next = new Set(prev);
            const k = keyOf(o);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };

    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(orders.map(keyOf)));

    const handleMove = () => {
        const picked = orders.filter((o) => selected.has(keyOf(o)));
        if (!picked.length) return;
        setSubmitting(true);
        setErrorMsg("");
        setResultMsg("");

        // Only durationMinutes travels with each order now — no
        // amountMeter/amountTime, since this move never touches the
        // capacity bar (no capacity check happens on the backend at
        // all — see scheduling.service.js's moveOrdersToNextDay). The
        // duration is still today's slice of the order (getDaySegment),
        // so the chained block on tomorrow's ruler is the same length
        // it would have needed today.
        const payloadOrders = picked.map((o) => {
            const seg = getDaySegment(o, dateKey);
            return {
                orderNo: o.orderNo,
                orderType: o.orderType,
                durationMinutes: seg ? seg.segEnd - seg.segStart : null,
            };
        });

        axios
            .post(`${ORDER_API}/scheduling/capacity/move-to-next-day`, { workcenter, date: dateKey, orders: payloadOrders })
            .then((res) => {
                const movedCount = (res.data.moved || []).length;
                const failed = res.data.failed || [];
                setResultMsg(
                    failed.length
                        ? `Moved ${movedCount} of ${picked.length} — ${failed.map((f) => `${f.orderNo} (${f.message})`).join("; ")}`
                        : `Moved ${movedCount} order${movedCount === 1 ? "" : "s"} to ${res.data.date}, starting 00:00. ` +
                          `The capacity bar there is still empty — drag each one onto it to allocate.`
                );
                // Drop the moved ones from the list right away instead of
                // leaving stale rows sitting there until the next
                // workcenter switch.
                const movedKeys = new Set((res.data.moved || []).map((m) => `${m.orderNo}::${m.orderType}`));
                setOrders((prev) => prev.filter((o) => !movedKeys.has(keyOf(o))));
                setSelected(new Set());
                if (onMoved) onMoved();
            })
            .catch((err) => setErrorMsg(err.response?.data?.message || "Failed to move orders"))
            .finally(() => setSubmitting(false));
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#fff", borderRadius: 12, width: 420, maxWidth: "90vw",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.18)", overflow: "hidden",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>Move to next day</div>
                    <button
                        onClick={onClose}
                        style={{ border: "none", background: "transparent", fontSize: 18, color: "#94a3b8", cursor: "pointer", lineHeight: 1, padding: 4 }}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase" }}>Workcenter</div>
                        <select
                            value={workcenter}
                            onChange={(e) => { setWorkcenter(e.target.value); setSelected(new Set()); setResultMsg(""); }}
                            style={{ width: "100%", fontSize: 13, padding: "7px 8px", borderRadius: 6, border: "1px solid #e2e8f0" }}
                        >
                            <option value="">Select a workcenter…</option>
                            {workcenterOptions.map((w) => (
                                <option key={w} value={w}>{w}</option>
                            ))}
                        </select>
                    </div>

                    {workcenter && (
                        <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                                    Remaining PL/PR on {workcenter}
                                </div>
                                {orders.length > 0 && (
                                    <label style={{ fontSize: 11.5, color: "#475569", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                                        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                                        Select all
                                    </label>
                                )}
                            </div>

                            <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #f1f5f9", borderRadius: 8 }}>
                                {loadingOrders ? (
                                    <div style={{ padding: 16, fontSize: 12, color: "#94a3b8", textAlign: "center" }}>Loading…</div>
                                ) : orders.length === 0 ? (
                                    <div style={{ padding: 16, fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                                        Nothing left over on this workcenter for {dateKey} — everything's already on the bar.
                                    </div>
                                ) : (
                                    orders.map((o) => (
                                        <label
                                            key={keyOf(o)}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                                                borderBottom: "1px solid #f8fafc", cursor: "pointer", fontSize: 12.5,
                                            }}
                                        >
                                            <input type="checkbox" checked={selected.has(keyOf(o))} onChange={() => toggleOne(o)} />
                                            <span style={{ fontWeight: 700, color: "#1e293b" }}>{o.orderNo}</span>
                                            <span style={{ color: "#94a3b8" }}>· {o.orderType === "Production Order" ? "PR" : "PL"}</span>
                                            {o.qty != null && <span style={{ color: "#94a3b8" }}>· {o.qty} m</span>}
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {errorMsg && <div style={{ fontSize: 12, color: "#dc2626" }}>{errorMsg}</div>}
                    {resultMsg && <div style={{ fontSize: 12, color: "#16a34a" }}>{resultMsg}</div>}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 18px", borderTop: "1px solid #f1f5f9" }}>
                    <button
                        onClick={onClose}
                        style={{ border: "1px solid #e2e8f0", background: "#fff", color: "#475569", borderRadius: 6, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
                    >
                        Close
                    </button>
                    <button
                        onClick={handleMove}
                        disabled={submitting || selected.size === 0}
                        style={{
                            border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 12.5, fontWeight: 700,
                            cursor: submitting || selected.size === 0 ? "not-allowed" : "pointer",
                            background: submitting || selected.size === 0 ? "#cbd5e1" : "#1e3a8a", color: "#fff",
                        }}
                    >
                        {submitting ? "Moving…" : `Move${selected.size ? ` (${selected.size})` : ""} to next day`}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Scheduling = () => {
    // Kept for when Planned Order's page bug is fixed and navigation
    // (instead of the inline popup) can be restored — unused for now.
    // eslint-disable-next-line no-unused-vars
    const navigate = useNavigate();

    const [allOrders, setAllOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    // Details table pagination — same "Page X of Y • Total N" + Prev/Next
    // pattern already used on the Purchase Request list.
    const [detailsPage, setDetailsPage] = useState(1);
    const DETAILS_PAGE_SIZE = 10;

    // Clicking a Production Order used to navigate to
    // /cable/production/production-order, which was logging the user out
    // (unrelated bug on that page's own auth handling). Production Order
    // clicks now open this inline popup instead — showing Workcenter and
    // Qty as requested — rather than leaving the Scheduling page at all.
    // Planned Order clicks still navigate to the Planned Order page as
    // before, since that wasn't reported as broken.
    const [selectedOrder, setSelectedOrder] = useState(null);

    // ── Dashboard/Details toggle ────────────────────────────────────────
    const [viewMode, setViewMode] = useState("dashboard"); // "dashboard" | "details"
    const now = new Date();
    // Single reference date drives the calendar — which month/week/day it's
    // currently showing — instead of separate month/year state, so Week and
    // Day navigation (which don't move in whole-month steps) both work off
    // the same value Month navigation uses.
    const [calendarRefDate, setCalendarRefDate] = useState(now);
    const [calendarGranularity, setCalendarGranularity] = useState("month"); // "month" | "week" | "day"
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState("");
    const [draggingOrder, setDraggingOrder] = useState(null); // order object currently being dragged
    const [dragOverDay, setDragOverDay] = useState(null); // "YYYY-MM-DD" the pointer is currently over

    // ── Capacity bar (Day view "Schedule By": By Meter / By Time) ──────
    // scheduleByFilter is purely a display-mode toggle, not a backend
    // list filter — it decides whether CapacityBar rows are shown at
    // all, so it isn't part of buildFilterParams below.
    const [scheduleByFilter, setScheduleByFilter] = useState(null);
    // Bumped after every successful allocate so every currently-mounted
    // CapacityBar refetches — otherwise moving an order off one
    // workcenter's bar would leave that bar showing stale (too-high)
    // totals until it happens to remount.
    const [capacityRefreshToken, setCapacityRefreshToken] = useState(0);
    // Refetches BOTH the capacity bars AND the calendar events after a
    // drop. Just bumping capacityRefreshToken wasn't enough — that only
    // reloads the bars; the order chip itself (its displayed time,
    // sourced from pegging via loadCalendar) never reloaded, so a
    // dropped order stayed marked "allocated" (yellow) while still
    // showing its OLD pre-chain time instead of the new chained one the
    // backend just wrote to pegging.
    const bumpCapacityRefresh = () => {
        setCapacityRefreshToken((t) => t + 1);
        if (viewMode === "dashboard") loadCalendar();
    };

    // How much of a workcenter's capacity bar this order would consume:
    // its qty (cable length in meters, matching Workcenter's own
    // "Capacity By Length" field) for By Meter, or its scheduled
    // duration in minutes (endTime - startTime) for By Time. Returns
    // null when the order doesn't carry what this mode needs — e.g. a
    // Planned Order with no machine time assigned yet has no
    // startTime/endTime — so CapacityBar can block the drop with a
    // clear reason instead of silently allocating 0.
    const getScheduleAmount = (order, scheduleBy) => {
        if (!order) return null;
        if (scheduleBy === "meter") {
            const q = Number(order.qty);
            return q > 0 ? q : null;
        }
        if (scheduleBy === "time") {
            const start = timeToMinutes(order.startTime);
            const end = timeToMinutes(order.endTime);
            if (start == null || end == null || end <= start) return null;
            return end - start;
        }
        return null;
    };

    // ── Cable-attribute filter master data (same source as Production
    // Order's listing page: generic config lookups on MASTER_API) ──────
    const [filterMasterData, setFilterMasterData] = useState({
        productTypes: [],
        constructions: [],
        codes: [],
        cableTypes: [],
        categories: [],
        cores: [],
        colours: [],
        nominalCrossSections: [],
        referenceOdStandards: [],
        voltages: [],
    });
    const [workcenterNames, setWorkcenterNames] = useState([]);
    const [productCodeMasterOptions, setProductCodeMasterOptions] = useState([]);

    // ── Filters ──────────────────────────────────────────────────────
    const [orderTypeFilter, setOrderTypeFilter] = useState(null); // null = All (Production Order + Planned Order)
    const [orderNoFilter, setOrderNoFilter] = useState(null);
    const [productCodeFilter, setProductCodeFilter] = useState(null);
    const [workcenterFilter, setWorkcenterFilter] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [productTypeFilter, setProductTypeFilter] = useState(null);
    const [cableAttrFilters, setCableAttrFilters] = useState({
        construction: null,
        code: null,
        cableType: null,
        category: null,
        noOfCores: null,
        color: null,
        nominalCrossSection: null,
        referenceOfStandard: null,
        voltage: null,
    });

    const handleCableAttrFilter = (key, opt) => {
        setCableAttrFilters((prev) => ({ ...prev, [key]: opt }));
    };

    // ── Load cable-attribute filter master data + Workcenter + Product
    // Code master options on mount — same endpoints ProductionOrder.js
    // uses for its own filter bar, so the option lists match exactly. ──
    useEffect(() => {
        const fetchFilterMasterData = async () => {
            try {
                const [
                    resProdTypes, resCons, resCode, resCable, resCat,
                    resCore, resCol, resCross, resRef, resVolt,
                ] = await Promise.all([
                    fetch(`${MASTER_API}/configuration/product-types`),
                    fetch(`${MASTER_API}/configuration/construction`),
                    fetch(`${MASTER_API}/configuration/dynamic/code`),
                    fetch(`${MASTER_API}/configuration/cable-type`),
                    fetch(`${MASTER_API}/configuration/category`),
                    fetch(`${MASTER_API}/configuration/core`),
                    fetch(`${MASTER_API}/configuration/colour`),
                    fetch(`${MASTER_API}/configuration/dynamic/nominal-cross-section`),
                    fetch(`${MASTER_API}/configuration/dynamic/reference-of-standard`),
                    fetch(`${MASTER_API}/configuration/dynamic/voltage`),
                ]);
                const prodTypesData = resProdTypes.ok ? await resProdTypes.json() : [];
                setFilterMasterData({
                    productTypes: prodTypesData.map((item) => ({ value: item.name, label: item.name })).filter((o) => o.value),
                    constructions: resCons.ok ? await resCons.json() : [],
                    codes: resCode.ok ? await resCode.json() : [],
                    cableTypes: resCable.ok ? await resCable.json() : [],
                    categories: resCat.ok ? await resCat.json() : [],
                    cores: resCore.ok ? await resCore.json() : [],
                    colours: resCol.ok ? await resCol.json() : [],
                    nominalCrossSections: resCross.ok ? await resCross.json() : [],
                    referenceOdStandards: resRef.ok ? await resRef.json() : [],
                    voltages: resVolt.ok ? await resVolt.json() : [],
                });
            } catch (err) {
                console.error("Failed fetching Scheduling filter master data", err);
            }
        };
        fetchFilterMasterData();

        axios
            .get(`${MASTER_API}/workcenter`)
            .then((res) => setWorkcenterNames((res.data.data || res.data).map((item) => item.workcenter_name)))
            .catch(console.error);

        // Product Code master list — same endpoint the Production Order
        // page's Product Code filter uses, so the dropdown matches.
        //
        // NOTE: unlike the Workcenter fetch above, this used to assume
        // `res.data` was already the array. If the backend actually wraps
        // the payload (e.g. `{ data: [...] }` or `{ success, data }`, the
        // same shape the Workcenter endpoint returns), `data.map(...)`
        // would throw, the promise would reject, `.catch(console.error)`
        // would swallow it silently, and the dropdown would show
        // "No options" with nothing visible to the user. Unwrap both
        // shapes the way the Workcenter fetch already does.
        axios
            .get(`${PURCHASE_API}/production-order/product-codes`)
            .then((res) => {
                const raw = res.data;
                const data = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
                const options = Array.from(
                    new Map(
                        data.map((item) => {
                            const code = String(item.product_code || item.productCode || "").trim();
                            const desc = String(item.productDescription || item.description || item.product_description || "").trim();
                            return [code, { value: code, label: desc ? `${code} — ${desc}` : code }];
                        })
                    ).values()
                ).filter((opt) => opt.value !== "");
                setProductCodeMasterOptions(options);
                if (options.length === 0) {
                    console.warn(
                        "Product Code master list came back empty — check the response shape of",
                        `${PURCHASE_API}/production-order/product-codes`
                    );
                }
            })
            .catch((err) => {
                console.error("Failed fetching Product Code master list", err);
            });
    }, []);

    // Shared by both the Details list and the Dashboard calendar, so
    // every filter in the bar above applies to whichever view is active.
    const buildFilterParams = () => ({
        orderType: orderTypeFilter ? orderTypeFilter.value : undefined,
        orderNo: orderNoFilter ? orderNoFilter.value : undefined,
        productCode: productCodeFilter ? productCodeFilter.value : undefined,
        startDate: fromDate || undefined,
        endDate: toDate || undefined,
        workcenter: workcenterFilter ? workcenterFilter.value : undefined,
        productType: productTypeFilter ? productTypeFilter.value : undefined,
        construction: cableAttrFilters.construction ? cableAttrFilters.construction.value : undefined,
        code: cableAttrFilters.code ? cableAttrFilters.code.value : undefined,
        cableType: cableAttrFilters.cableType ? cableAttrFilters.cableType.value : undefined,
        category: cableAttrFilters.category ? cableAttrFilters.category.value : undefined,
        noOfCores: cableAttrFilters.noOfCores ? cableAttrFilters.noOfCores.value : undefined,
        color: cableAttrFilters.color ? cableAttrFilters.color.value : undefined,
        nominalCrossSection: cableAttrFilters.nominalCrossSection ? cableAttrFilters.nominalCrossSection.value : undefined,
        referenceOfStandard: cableAttrFilters.referenceOfStandard ? cableAttrFilters.referenceOfStandard.value : undefined,
        voltage: cableAttrFilters.voltage ? cableAttrFilters.voltage.value : undefined,
    });

    const loadSchedule = useCallback(() => {
        setLoading(true);
        setErrorMsg("");
        axios
            .get(`${ORDER_API}/scheduling/list`, { params: buildFilterParams() })
            .then((res) => {
                setAllOrders(res.data || []);
            })
            .catch((err) => {
                console.error("Failed to load scheduling list", err);
                setErrorMsg("Failed to load scheduling data. Please try again.");
            })
            .finally(() => setLoading(false));
    }, [
        orderTypeFilter, orderNoFilter, productCodeFilter, fromDate, toDate,
        workcenterFilter, productTypeFilter, cableAttrFilters,
    ]);

    useEffect(() => {
        loadSchedule();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadSchedule]);

    // ── Dashboard calendar: compute the visible date range from
    // calendarRefDate + calendarGranularity, load events for that range
    // (filtered by the same filter bar as Details), only while the
    // Dashboard tab is actually visible. ────────────────────────────
    const pad2 = (n) => String(n).padStart(2, "0");
    const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

    const getCalendarRange = useCallback(() => {
        const ref = calendarRefDate;
        if (calendarGranularity === "day") {
            return { start: toISODate(ref), end: toISODate(ref) };
        }
        if (calendarGranularity === "week") {
            const start = new Date(ref);
            start.setDate(start.getDate() - start.getDay()); // back up to Sunday
            const end = new Date(start);
            end.setDate(end.getDate() + 6); // forward to Saturday
            return { start: toISODate(start), end: toISODate(end) };
        }
        // month — cover the full 6-week grid (including the leading/trailing
        // days from adjacent months that the grid actually renders), so a
        // chip dropped on one of those visible-but-adjacent days still has
        // correct data if you navigate there directly.
        const firstOfMonth = new Date(ref.getFullYear(), ref.getMonth(), 1);
        const gridStart = new Date(firstOfMonth);
        gridStart.setDate(gridStart.getDate() - firstOfMonth.getDay());
        const gridEnd = new Date(gridStart);
        gridEnd.setDate(gridEnd.getDate() + 41); // 6 weeks * 7 days - 1
        return { start: toISODate(gridStart), end: toISODate(gridEnd) };
    }, [calendarRefDate, calendarGranularity]);

    const loadCalendar = useCallback(() => {
        setCalendarLoading(true);
        setCalendarError("");
        const { start, end } = getCalendarRange();
        axios
            .get(`${ORDER_API}/scheduling/calendar`, {
                params: { ...buildFilterParams(), start, end },
            })
            .then((res) => setCalendarEvents(res.data.events || []))
            .catch((err) => {
                console.error("Failed to load scheduling calendar", err);
                setCalendarError("Failed to load calendar. Please try again.");
            })
            .finally(() => setCalendarLoading(false));
    }, [
        getCalendarRange,
        orderTypeFilter, orderNoFilter, productCodeFilter, fromDate, toDate,
        workcenterFilter, productTypeFilter, cableAttrFilters,
    ]);

    useEffect(() => {
        if (viewMode === "dashboard") loadCalendar();
    }, [viewMode, loadCalendar]);

    // Prev/Next step by whatever unit is currently selected — a whole
    // month, a week, or a single day — same idea as switching Audit
    // Calendar's month nav to also handle Week/Day granularity.
    const goToPrev = () => {
        setCalendarRefDate((d) => {
            const next = new Date(d);
            if (calendarGranularity === "month") next.setMonth(next.getMonth() - 1);
            else if (calendarGranularity === "week") next.setDate(next.getDate() - 7);
            else next.setDate(next.getDate() - 1);
            return next;
        });
    };
    const goToNext = () => {
        setCalendarRefDate((d) => {
            const next = new Date(d);
            if (calendarGranularity === "month") next.setMonth(next.getMonth() + 1);
            else if (calendarGranularity === "week") next.setDate(next.getDate() + 7);
            else next.setDate(next.getDate() + 1);
            return next;
        });
    };
    const goToToday = () => setCalendarRefDate(new Date());
    const jumpToYear = (y) => {
        setCalendarRefDate((d) => {
            const next = new Date(d);
            next.setFullYear(Number(y));
            return next;
        });
    };
    // Jump straight to an arbitrary date (from the <input type="date">
    // picker) — parses the "YYYY-MM-DD" value as a local date, same
    // caution as toDateKey/safeDate, to avoid a UTC off-by-one.
    const jumpToDate = (dateStr) => {
        if (!dateStr) return;
        const [y, m, d] = dateStr.split("-").map(Number);
        if (!y || !m || !d) return;
        setCalendarRefDate(new Date(y, m - 1, d));
    };

    // Drag-and-drop reschedule — PUT the new date, then reload the
    // calendar so the moved chip reflects the server's actual state
    // (rather than optimistically mutating local state), same
    // "trust the write, then refetch" approach rescheduleInspection's
    // callers use on the Audit Calendar.
    const handleDropOnDay = (dateStr) => {
        setDragOverDay(null);
        if (!draggingOrder || !dateStr) { setDraggingOrder(null); return; }
        const order = draggingOrder;
        setDraggingOrder(null);
        if (order.startDate) {
            const existing = new Date(order.startDate);
            if (!isNaN(existing.getTime())) {
                const pad = (n) => String(n).padStart(2, "0");
                const existingStr = `${existing.getFullYear()}-${pad(existing.getMonth() + 1)}-${pad(existing.getDate())}`;
                if (existingStr === dateStr) return; // dropped back on the same day — no-op
            }
        }
        axios
            .put(`${ORDER_API}/scheduling/reschedule`, {
                orderNo: order.orderNo,
                orderType: order.orderType,
                date: dateStr,
            })
            .then(() => {
                loadCalendar();
                loadSchedule(); // keep Details table in sync too
            })
            .catch((err) => {
                console.error("Failed to reschedule order", err);
                setCalendarError(
                    err?.response?.data?.message || "Failed to reschedule that order. Please try again."
                );
            });
    };

    // Order dropdown: built from whatever is currently loaded, filtered by
    // Order Type if one is selected — "Order" shows the planned order
    // number or production order number.
    const orderNoOptions = allOrders
        .filter((o) => !orderTypeFilter || o.orderType === orderTypeFilter.value)
        .map((o) => ({ value: o.orderNo, label: o.orderNo, startDate: o.startDate || o.endDate || null }))
        .filter((opt, idx, arr) => opt.value && arr.findIndex((a) => a.value === opt.value) === idx);

    const visibleOrders = allOrders;

    // Reset to page 1 whenever the underlying filtered list changes size —
    // otherwise switching filters while on, say, page 3 could land on a
    // page that no longer exists and silently show nothing.
    useEffect(() => {
        setDetailsPage(1);
    }, [visibleOrders.length]);

    const detailsTotalPages = Math.max(1, Math.ceil(visibleOrders.length / DETAILS_PAGE_SIZE));
    const detailsPageClamped = Math.min(detailsPage, detailsTotalPages);
    const paginatedOrders = visibleOrders.slice(
        (detailsPageClamped - 1) * DETAILS_PAGE_SIZE,
        detailsPageClamped * DETAILS_PAGE_SIZE
    );

    // Fallback Product Code options built from whatever is currently
    // loaded in the schedule itself. This guarantees the dropdown has
    // something in it even if the `${PURCHASE_API}/production-order/product-codes`
    // master-data endpoint is down, not yet wired up, or returns a
    // response shape this page doesn't expect. Formatted the same
    // "code — description" way the master list is, using whatever
    // description the row already carries (the Description column in
    // the table below) — if a given order has none, it just falls back
    // to showing the code alone.
    const fallbackProductCodeOptions = Array.from(
        new Map(
            allOrders
                .filter((o) => o.productCode)
                .map((o) => {
                    const code = String(o.productCode).trim();
                    const desc = String(o.description || "").trim();
                    return [code, { value: code, label: desc ? `${code} — ${desc}` : code }];
                })
        ).values()
    );

    const productCodeOptions =
        productCodeMasterOptions.length > 0 ? productCodeMasterOptions : fallbackProductCodeOptions;

    // Both order types now open the inline popup instead of navigating —
    // Production Order already did this because navigating to its page
    // was logging the user out (see PRODUCTION_ORDER_ROUTE comment
    // above); Planned Order's route has the same bug, so it gets the
    // same workaround here rather than hitting PLANNED_ORDER_ROUTE.
    // OrderDetailPopup's fields (workcenter, qty, productCode,
    // description, dates, status) are already generic across both order
    // types, so no popup changes were needed.
    const openOrder = (order) => {
        setSelectedOrder(order);
    };

    return (
        <Layout defaultSidebarOpen={false}>
            <div style={{ padding: "20px 24px", maxWidth: "100%", boxSizing: "border-box" }}>
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 10,
                        marginBottom: 12,
                        background: "#fff",
                        borderRadius: 10,
                        padding: "10px 16px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    }}
                >
                    <div>
                        <h2 style={{ fontSize: 17, fontWeight: 700, color: "#1e293b", margin: 0 }}>Scheduling</h2>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 1 }}>
                            Production Orders and Planned Orders in one schedule
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 5, background: "#f1f5f9", borderRadius: 7, padding: 3 }}>
                        {[
                            { key: "dashboard", label: "Dashboard" },
                            { key: "details", label: "Details" },
                        ].map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setViewMode(t.key)}
                                style={{
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "5px 13px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    background: viewMode === t.key ? "#1e293b" : "transparent",
                                    color: viewMode === t.key ? "#fff" : "#475569",
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Filters — same layout/fields as the Production Order
                    listing page's filter bar, plus an Order Type selector
                    since Scheduling mixes Production Orders and Planned
                    Orders in one list. */}
                <div
                    style={{
                        background: "#fff",
                        borderRadius: 10,
                        padding: "8px 16px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        marginBottom: 10,
                    }}
                >
                    {/* Row 0: Product Type */}
                    <div className="row mb-2">
                        <div className="col-md-3">
                            <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                Product Type
                            </label>
                            <Select
                                options={filterMasterData.productTypes}
                                value={productTypeFilter}
                                onChange={setProductTypeFilter}
                                isClearable
                                placeholder="Select Product Type"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={filterSelectStyles}
                            />
                        </div>
                    </div>

                    {/* Row 1: Cable filters — Construction, Code, Cable Type,
                        Category, Cores, Color, Cross Section, Std. Ref, Voltage */}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 6 }}>
                        {[
                            { key: "construction", opts: filterMasterData.constructions, label: "Construction" },
                            { key: "code", opts: filterMasterData.codes, label: "Code" },
                            { key: "cableType", opts: filterMasterData.cableTypes, label: "Cable Type" },
                            { key: "category", opts: filterMasterData.categories, label: "Category" },
                            { key: "noOfCores", opts: filterMasterData.cores, label: "Cores" },
                            { key: "color", opts: filterMasterData.colours, label: "Color" },
                            { key: "nominalCrossSection", opts: filterMasterData.nominalCrossSections, label: "Cross Section" },
                            { key: "referenceOfStandard", opts: filterMasterData.referenceOdStandards, label: "Std. Ref" },
                            { key: "voltage", opts: filterMasterData.voltages, label: "Voltage" },
                        ].map(({ key, opts, label }) => (
                            <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                    {label}
                                </label>
                                <Select
                                    placeholder={label}
                                    isClearable
                                    options={getFilterOptions(opts)}
                                    value={cableAttrFilters[key]}
                                    onChange={(opt) => handleCableAttrFilter(key, opt)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={filterSelectStyles}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Row 2: Order Type | Order | Product Code | Workcenter | From | To */}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                Order Type
                            </label>
                            <Select
                                options={ORDER_TYPE_OPTIONS}
                                value={orderTypeFilter}
                                onChange={(opt) => {
                                    setOrderTypeFilter(opt);
                                    setOrderNoFilter(null); // reset order pick when type changes
                                }}
                                isClearable
                                placeholder="All"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={filterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                Order
                            </label>
                            <Select
                                options={orderNoOptions}
                                value={orderNoFilter}
                                onChange={(opt) => {
                                    setOrderNoFilter(opt);
                                    // Without this, picking an order while
                                    // the Dashboard calendar is showing some
                                    // unrelated day reads as "search is
                                    // broken" — it isn't, the calendar was
                                    // just never told to look at the day
                                    // this order is actually on.
                                    if (opt && opt.startDate) {
                                        const d = parseLocalDate(opt.startDate);
                                        if (d) setCalendarRefDate(d);
                                    }
                                }}
                                isClearable
                                isSearchable
                                placeholder="Production or Planned order no."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={filterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                Product Code
                            </label>
                            <Select
                                options={productCodeOptions}
                                value={productCodeFilter}
                                onChange={setProductCodeFilter}
                                isClearable
                                placeholder="Product Code"
                                noOptionsMessage={() => "No product codes found"}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={filterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                Workcenter
                            </label>
                            <Select
                                options={workcenterNames.map((w) => ({ value: w, label: w }))}
                                value={workcenterFilter}
                                onChange={setWorkcenterFilter}
                                isClearable
                                placeholder="Workcenter"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={filterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                Schedule By
                            </label>
                            <Select
                                options={SCHEDULE_BY_OPTIONS}
                                value={scheduleByFilter}
                                onChange={setScheduleByFilter}
                                isClearable
                                placeholder="Off"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={filterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                From
                            </label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => {
                                    setFromDate(e.target.value);
                                    if (!e.target.value) setToDate("");
                                }}
                                style={{
                                    width: "100%", height: 26, fontSize: 11, border: "1px solid #e2e8f0",
                                    borderRadius: 5, padding: "0 6px", color: "#1e293b", background: "#fff",
                                    boxSizing: "border-box", cursor: "pointer",
                                }}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>
                                To
                            </label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                disabled={!fromDate}
                                style={{
                                    width: "100%", height: 26, fontSize: 11, border: "1px solid #e2e8f0",
                                    borderRadius: 5, padding: "0 6px", boxSizing: "border-box",
                                    color: !fromDate ? "#94a3b8" : "#1e293b",
                                    background: !fromDate ? "#f1f5f9" : "#fff",
                                    cursor: !fromDate ? "not-allowed" : "pointer",
                                }}
                            />
                        </div>

                    </div>
                </div>

                {viewMode === "dashboard" ? (
                    <SchedulingCalendar
                        refDate={calendarRefDate}
                        granularity={calendarGranularity}
                        setGranularity={setCalendarGranularity}
                        events={calendarEvents}
                        loading={calendarLoading}
                        errorMsg={calendarError}
                        onPrev={goToPrev}
                        onNext={goToNext}
                        onToday={goToToday}
                        onJumpToYear={jumpToYear}
                        onJumpToDate={jumpToDate}
                        onOpenOrder={openOrder}
                        draggingOrder={draggingOrder}
                        setDraggingOrder={setDraggingOrder}
                        dragOverDay={dragOverDay}
                        setDragOverDay={setDragOverDay}
                        onDropOnDay={handleDropOnDay}
                        scheduleByFilter={scheduleByFilter}
                        getScheduleAmount={getScheduleAmount}
                        capacityRefreshToken={capacityRefreshToken}
                        onAllocated={bumpCapacityRefresh}
                        workcenterOptions={workcenterNames}
                    />
                ) : (
                <div
                    style={{
                        background: "#ffffff",
                        borderRadius: 10,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                    }}
                >                    <div style={{ overflowX: "auto", width: "100%" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                            <thead>
                                <tr style={{ background: "#f8fafc" }}>
                                    <th style={erpTh}>Receipt</th>
                                    <th style={erpTh}>Receipt Type</th>
                                    <th style={erpTh}>Workcenter</th>
                                    <th style={erpTh}>Product Code</th>
                                    <th style={erpTh}>Description</th>
                                    <th style={{ ...erpTh, textAlign: "right" }}>Qty</th>
                                    <th style={erpTh}>Start Date</th>
                                    <th style={erpTh}>End Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" style={{ ...erpTd, textAlign: "center", color: "#94a3b8", padding: 24 }}>
                                            Loading schedule...
                                        </td>
                                    </tr>
                                ) : errorMsg ? (
                                    <tr>
                                        <td colSpan="8" style={{ ...erpTd, textAlign: "center", color: "#dc2626", padding: 24 }}>
                                            {errorMsg}
                                        </td>
                                    </tr>
                                ) : visibleOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ ...erpTd, textAlign: "center", color: "#94a3b8", padding: 24 }}>
                                            No production or planned orders found.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedOrders.map((o, i) => (
                                        <tr
                                            key={`${o.orderType}-${o.orderNo}-${i}`}
                                            style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafbfc")}
                                        >
                                            <td style={{ ...erpTd, fontWeight: 600, color: "#1e293b" }}>
                                                <span
                                                    onClick={() => openOrder(o)}
                                                    style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                >
                                                    {o.orderNo}
                                                </span>
                                            </td>
                                            <td style={erpTd}>
                                                <OrderTypeBadge type={o.orderType} />
                                            </td>
                                            <td style={erpTd}>{o.workcenter || ""}</td>
                                            <td style={erpTd}>{o.productCode || ""}</td>
                                            <td
                                                style={{ ...erpTd, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                title={o.description || ""}
                                            >
                                                {o.description || ""}
                                            </td>
                                            <td style={{ ...erpTd, textAlign: "right", fontWeight: 600 }}>{o.qty ?? ""}</td>
                                            <td style={erpTd}>{formatDMY(o.startDate)}</td>
                                            <td style={erpTd}>{formatDMY(o.endDate)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination — same pattern as the Purchase Request list */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 16px",
                            borderTop: "1px solid #f1f5f9",
                            fontSize: 12.5,
                            color: "#64748b",
                        }}
                    >
                        <div>
                            Page <strong style={{ color: "#1e293b" }}>{detailsPageClamped}</strong> of{" "}
                            <strong style={{ color: "#1e293b" }}>{detailsTotalPages}</strong>
                            {" · "}Total <strong style={{ color: "#1e293b" }}>{visibleOrders.length}</strong>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                onClick={() => setDetailsPage((p) => Math.max(1, p - 1))}
                                disabled={detailsPageClamped <= 1}
                                style={{
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 6,
                                    padding: "6px 16px",
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    background: "#fff",
                                    color: detailsPageClamped <= 1 ? "#cbd5e1" : "#334155",
                                    cursor: detailsPageClamped <= 1 ? "not-allowed" : "pointer",
                                }}
                            >
                                Prev
                            </button>
                            <button
                                onClick={() => setDetailsPage((p) => Math.min(detailsTotalPages, p + 1))}
                                disabled={detailsPageClamped >= detailsTotalPages}
                                style={{
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 6,
                                    padding: "6px 16px",
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    background: "#fff",
                                    color: detailsPageClamped >= detailsTotalPages ? "#cbd5e1" : "#334155",
                                    cursor: detailsPageClamped >= detailsTotalPages ? "not-allowed" : "pointer",
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}
            </div>

            <OrderDetailPopup order={selectedOrder} onClose={() => setSelectedOrder(null)} />
        </Layout>
    );
};

export default Scheduling;